/**
 * Before the first visit: tell the owner tracking is already on, and invite
 * them to be visitor number one. The live dot flips green as soon as YOM sees
 * them (the page polls realtime every 10 s in this state, then swaps in the
 * full dashboard).
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Loader2, Radio, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { RealtimeData } from '@/lib/yom/reportClient';
import { LiveDot, peopleNow } from './LiveNow';

function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

export function FirstRunState({
  collectingSince,
  siteUrl,
  live,
}: {
  collectingSince: string | null;
  siteUrl: string | null;
  live: RealtimeData | undefined;
}) {
  const since = formatDay(collectingSince);
  const n = live?.active_visitors ?? 0;
  return (
    <div className="bg-card overflow-hidden rounded-xl border shadow">
      <div className="flex flex-col items-center px-6 py-12 text-center sm:py-16">
        <div className="bg-primary/10 text-primary mb-5 rounded-full p-3">
          <Radio className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="text-xl font-semibold tracking-tight">
          {since ? `Collecting since ${since}` : 'Your analytics are switched on'}
        </h2>
        <p className="text-muted-foreground mt-2 max-w-md text-sm leading-relaxed">
          Visit your site and you&apos;ll show up here within a minute. Visitors, leads, calls and
          orders will appear on this page as they happen.
        </p>
        <div
          className="bg-muted/60 mt-6 inline-flex items-center gap-2.5 rounded-full px-4 py-2 text-sm"
          aria-live="polite"
        >
          <LiveDot active={n > 0} />
          {live ? peopleNow(n) : 'Listening for visitors…'}
        </div>
        {siteUrl && (
          <Button asChild className="mt-6">
            <a href={siteUrl} target="_blank" rel="noopener noreferrer">
              Open my site
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

/** Managed analytics are on, but this site's account is still being created. */
export function SettingUpState({
  projectId,
  onCheckAgain,
  checking,
}: {
  projectId: string;
  onCheckAgain: () => void;
  checking: boolean;
}) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <Link
          to={`/projects/${projectId}`}
          className="hover:bg-muted -ml-2 mt-0.5 rounded-lg p-2 transition-colors"
          aria-label="Back to project"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
      </div>
      <div className="bg-card flex flex-col items-center rounded-xl border px-6 py-12 text-center shadow sm:py-16">
        <div className="bg-primary/10 text-primary mb-5 rounded-full p-3">
          <Sparkles className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="text-xl font-semibold tracking-tight">
          We&apos;re setting up your analytics
        </h2>
        <p className="text-muted-foreground mt-2 max-w-md text-sm leading-relaxed">
          There&apos;s nothing for you to install. This usually takes a few minutes, and your
          visitors will start showing up here once it&apos;s done.
        </p>
        <Button variant="outline" className="mt-6" onClick={onCheckAgain} disabled={checking}>
          {checking && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
          Check again
        </Button>
      </div>
    </div>
  );
}
